import { Outlet, useLocation, useNavigate } from "react-router";
import { isApiError } from "./api/client";
import { AppHeader, type MainSection } from "./components/AppHeader";
import { PanelStatus } from "./components/PanelStatus";
import { runtimeConfig } from "./config/runtime";
import type { User } from "./data/mock";
import { initialsOf, toUser } from "./features/auth/adapt";
import { useLogout, useMe } from "./features/auth/api";

const SECTIONS: readonly MainSection[] = ["news", "ricerca", "alert"];

/**
 * The section of the header from the path: /news, /ricerca, /alert (anything
 * else counts as news, except the guide, which belongs to no section).
 */
function sectionOf(pathname: string): MainSection | null {
  const first = pathname.split("/")[1] ?? "";
  if (first === "guida") return null;
  return SECTIONS.find((s) => s === first) ?? "news";
}

export const LOADING_SESSION = "Accesso in corso…";
const SESSION_FAILED = "Non riesco a verificare l'accesso.";

/**
 * The layout: the header and the page frame; the page is the route's child.
 * With entra nothing shows before /me answers: a 401 has taken the window to
 * the login already (src/api/client.ts), any other failure offers «Riprova».
 * In dev mode the page does not wait: the avatar names the configured test
 * user until /me gives the name.
 */
export default function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const section = sectionOf(pathname);
  const { authMode, devUser, version } = runtimeConfig();
  const me = useMe();
  const logout = useLogout();

  if (authMode === "entra" && !me.data) {
    if (me.isError && !(isApiError(me.error) && me.error.status === 401)) {
      return (
        <PanelStatus role="alert" indent={false} onRetry={() => void me.refetch()}>
          {SESSION_FAILED}
        </PanelStatus>
      );
    }
    return <PanelStatus indent={false}>{LOADING_SESSION}</PanelStatus>;
  }
  const user: User = me.data ? toUser(me.data) : { name: devUser, email: "", initials: initialsOf(devUser) };

  return (
    <div className="flex min-h-screen flex-col">
      <AppHeader
        user={user}
        section={section}
        onSectionChange={(next) => void navigate(`/${next}`)}
        onGuide={() => void navigate("/guida")}
        onLogout={authMode === "entra" ? () => logout.mutate() : undefined}
        build={version}
      />
      <div className="mx-auto flex w-full max-w-[1354px] flex-1 flex-col px-4 pt-12">
        <Outlet />
      </div>
    </div>
  );
}
